import { useCallback } from 'react';

import { Trash2 } from 'lucide-react';
import { UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  formatAmountWithCurrencyCode,
  formatTrimmedDecimal,
  parseFormNumber,
  roundToPrecision,
} from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { LineValidationTooltip } from '@/modules/finance/common';
import { FINANCE_QUANTITY_MAX_DECIMAL_PLACES } from '@/modules/finance/common/constants/line-item-validation.constants';
import {
  FINANCE_MAX_INTEGER_DIGITS,
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { getShortTeamMembers } from '@/modules/team';
import { NON_ARCHIVED_TEAM_MEMBER_STATUSES } from '@/modules/team/types/dto/find-short-team-members.request';

import { InvoiceDetails, InvoiceEntityType } from '../../types';
import {
  InvoiceLineValidationIssue,
  UPDATE_INVOICE_LINE_MESSAGE_KEY_BY_ISSUE_CODE,
  UpdateInvoiceFormValues,
} from '../../validations';

interface InvoiceEditItemRowProps {
  details: InvoiceDetails;
  form: UseFormReturn<UpdateInvoiceFormValues>;
  index: number;
  currencyCode: string;
  validationIssues: InvoiceLineValidationIssue[];
  onRemove: () => void;
}

/**
 * Renders one editable invoice line using member-specific or overhead-specific controls.
 * Member and overhead amounts are derived from quantity and rate; overheads use one fixed hour.
 */
export function InvoiceEditItemRow({
  details,
  form,
  index,
  currencyCode,
  validationIssues,
  onRemove,
}: InvoiceEditItemRowProps) {
  const { t } = useTranslation(['authenticated.invoices', 'common']);
  const item = useWatch({ control: form.control, name: `items.${index}` });
  const { errors } = useFormState({ control: form.control, name: `items.${index}` });
  const itemErrors = errors.items?.[index];
  const initialMember = details.items.find(
    detailsItem =>
      detailsItem.entityType === InvoiceEntityType.User &&
      detailsItem.entityId?.toString() === item?.entityId
  )?.member;
  const isUser = item?.entityType === InvoiceEntityType.User;
  const isOverhead = item?.entityType === InvoiceEntityType.Overhead;
  const validationMessages = [
    ...new Set([
      ...validationIssues.map(issue =>
        t(UPDATE_INVOICE_LINE_MESSAGE_KEY_BY_ISSUE_CODE[issue.code])
      ),
      ...(itemErrors?.entityType?.message ? [t(itemErrors.entityType.message)] : []),
    ]),
  ];
  const amount =
    isUser || isOverhead
      ? roundToPrecision(
          parseFormNumber(item.quantity) * parseFormNumber(item.unitPrice),
          FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
        ).toString()
      : item.amount;

  const loadMembers = useCallback(
    (cursor?: number | null) =>
      getShortTeamMembers({
        filter: { statuses: NON_ARCHIVED_TEAM_MEMBER_STATUSES },
        pagination: { cursor },
      }),
    []
  );
  const searchMembers = useCallback(
    (searchTerm: string, cursor?: number | null) =>
      getShortTeamMembers({
        filter: { searchString: searchTerm, statuses: NON_ARCHIVED_TEAM_MEMBER_STATUSES },
        pagination: { cursor },
      }),
    []
  );

  return (
    <TableRow>
      <TableCell className="min-w-64">
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">
            {isUser ? (
              <SingleSelectWithPagination<UpdateInvoiceFormValues>
                name={`items.${index}.entityId`}
                control={form.control}
                placeholder={t('details.edit.placeholders.member')}
                queryKey={['invoice-edit-members']}
                loadItems={loadMembers}
                searchItems={searchMembers}
                showAvatars
                avatarSize="sm"
                className="py-1"
                showFormMessage={false}
                initialSelectedItem={
                  initialMember
                    ? {
                        id: initialMember.id.toString(),
                        name: initialMember.name,
                        avatarLink: initialMember.profileImageUrl ?? undefined,
                        secondaryText: initialMember.email ?? undefined,
                      }
                    : undefined
                }
              />
            ) : (
              <FormField
                control={form.control}
                name={`items.${index}.description`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        aria-label={t('details.edit.items.description')}
                        disabled={!isOverhead}
                        className={cn(itemErrors?.description && 'border-destructive')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </div>
          <div className="flex h-4 w-4 shrink-0 items-center justify-center">
            <LineValidationTooltip messages={validationMessages} />
          </div>
        </div>
      </TableCell>

      <TableCell className="w-40">
        {isUser ? (
          <div className={itemErrors?.quantity ? '[&_input]:border-destructive' : ''}>
            <InputFieldWithCurrency
              name={`items.${index}.quantity`}
              control={form.control}
              isReadMode={false}
              valueType="string"
              showFormMessage={false}
              maxDecimalPlaces={FINANCE_QUANTITY_MAX_DECIMAL_PLACES}
              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
              minValue={0}
            />
          </div>
        ) : isOverhead ? (
          <span className="min-w-28 max-w-36 tabular-nums">{formatTrimmedDecimal(1)}</span>
        ) : (
          <Input
            value={t('common:table.noValue')}
            aria-label={t('details.lineItemsHeaders.totalHours')}
            disabled
            className="min-w-28 max-w-36"
          />
        )}
      </TableCell>

      <TableCell className="w-56 min-w-0">
        {isUser ? (
          <div className={itemErrors?.unitPrice ? '[&_input]:border-destructive' : ''}>
            <InputFieldWithCurrency
              name={`items.${index}.unitPrice`}
              control={form.control}
              isReadMode={false}
              valueType="string"
              showFormMessage={false}
              suffixText={t('common:rateUnits.perHour', { currency: currencyCode })}
              maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
              minValue={0}
            />
          </div>
        ) : isOverhead ? (
          <div className={itemErrors?.unitPrice ? '[&_input]:border-destructive' : ''}>
            <InputFieldWithCurrency
              name={`items.${index}.unitPrice`}
              control={form.control}
              isReadMode={false}
              valueType="string"
              showFormMessage={false}
              suffixText={currencyCode}
              maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
              allowNegative
              allowEmpty
            />
          </div>
        ) : (
          <Input
            value={t('common:table.noValue')}
            aria-label={t('details.lineItemsHeaders.effectiveRate')}
            disabled
          />
        )}
      </TableCell>

      <TableCell className="w-40">
        {isUser || isOverhead ? (
          formatAmountWithCurrencyCode(amount, currencyCode)
        ) : (
          <div className={itemErrors?.amount ? '[&_input]:border-destructive' : ''}>
            <InputFieldWithCurrency
              name={`items.${index}.amount`}
              control={form.control}
              isReadMode={false}
              valueType="string"
              showFormMessage={false}
              suffixText={currencyCode}
              maxDecimalPlaces={FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES}
              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
              allowNegative={isOverhead}
            />
          </div>
        )}
      </TableCell>

      <TableCell className="w-24 pr-6">
        <div className="flex items-center justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            aria-label={t('details.edit.items.remove')}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
